import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Button from '../Button';

interface GiftInfoFormData {
  receiver_name: string;
  receiver_email: string;
  receiver_phone: string;
}

interface GiftInfoModalProps {
  open: boolean;
  deliveryRequest: string;
  onClose: () => void;
  onConfirm: (data: GiftInfoFormData) => void;
}

const GiftInfoModal: React.FC<GiftInfoModalProps> = ({ open, onClose, onConfirm, deliveryRequest }) => {
  const { t } = useTranslation();
  const [draftData, setDraftData] = useState<GiftInfoFormData | null>(null);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const schema = yup.object().shape({
    receiver_name: yup.string().required(t('RECEIVER_NAME_REQUIRED')),
    receiver_email: yup
      .string()
      .required(t('RECEIVER_INFO_REQUIRED'))
      .test('email', t('RECEIVER_EMAIL_INVALID'), function (value) {
        return !!value && emailRegex.test(value);
      }),
    receiver_phone: yup
      .string()
      .required(t('RECEIVER_INFO_REQUIRED'))
      .test('phone-validation', t('RECEIVER_PHONE_INVALID'), function (value) {
        if (!value || value.trim() === '') return true;
        const cleanedValue = value.replace(/-/g, '');
        const phoneRegex = /^0\d{9,10}$/;
        return phoneRegex.test(cleanedValue);
      }),
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<GiftInfoFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      receiver_name: '',
      receiver_email: '',
      receiver_phone: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (draftData) {
        reset(draftData);
      } else {
        reset({
          receiver_name: '',
          receiver_email: '',
          receiver_phone: '',
        });
      }
    }
  }, [open, draftData, reset]);

  const handleClose = () => {
    const currentValues = getValues();
    setDraftData(currentValues);
    onClose();
  };

  const onSubmit = handleSubmit((data: GiftInfoFormData) => {
    onConfirm(data);
    setDraftData(null);
    onClose();
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          margin: 2,
          maxHeight: 'calc(100dvh - 32px)',
        },
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold text-gray-900">{t('GIFT_INFO_TITLE')}</h2>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      {/* Content */}
      <DialogContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('DELIVERY_DATE')}</label>
            <div className="w-full box-border p-[10px] text-base leading-[22px] border border-[#D0D5DD] rounded-lg bg-gray-50">{deliveryRequest}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('RECEIVER_NAME_KANA')}</label>
            <Controller
              name="receiver_name"
              control={control}
              render={({ field }) => (
                <>
                  <input
                    {...field}
                    placeholder={t('RECEIVER_NAME_PLACEHOLDER')}
                    className="w-full box-border p-[10px] text-base leading-[22px] border border-[#D0D5DD] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#3D9F4A] hover:border-[#9ca3af] cursor-pointer"
                  />
                  {errors.receiver_name && <p className="mt-1 text-sm text-red-600">{errors.receiver_name.message}</p>}
                </>
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('RECEIVER_EMAIL')}</label>
            <Controller
              name="receiver_email"
              control={control}
              render={({ field }) => (
                <>
                  <input
                    {...field}
                    placeholder={t('RECEIVER_EMAIL_PLACEHOLDER')}
                    className="w-full box-border p-[10px] text-base leading-[22px] border border-[#D0D5DD] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#3D9F4A] hover:border-[#9ca3af] cursor-pointer"
                  />
                  {errors.receiver_email && <p className="mt-1 text-sm text-red-600">{errors.receiver_email.message}</p>}
                </>
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('RECEIVER_PHONE')}</label>
            <Controller
              name="receiver_phone"
              control={control}
              render={({ field }) => (
                <>
                  <input
                    {...field}
                    placeholder={t('RECEIVER_PHONE_PLACEHOLDER')}
                    className="w-full box-border p-[10px] text-base leading-[22px] border border-[#D0D5DD] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#3D9F4A] hover:border-[#9ca3af] cursor-pointer"
                  />
                  {errors.receiver_phone && <p className="mt-1 text-sm text-red-600">{errors.receiver_phone.message}</p>}
                </>
              )}
            />
          </div>

          {/* Warning Alert */}
          <div className="flex gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="text-sm text-gray-700">
              <p className="font-semibold mb-1">{t('GIFT_INFO_WARNING_TITLE')}</p>
              <p>{t('GIFT_INFO_WARNING_MESSAGE')}</p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <Button type="secondary">{t('GIFT_CONFIRM')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GiftInfoModal;
